import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Milestone, X, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PROJECT_STATUS, STATUS, formatDate, initials } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tab, setTab] = useState("actividades");
  const [showResp, setShowResp] = useState(false);
  const [showAct, setShowAct] = useState(false);
  const [showMil, setShowMil] = useState(false);
  const [respForm, setRespForm] = useState({ user_id:"", role_name:"", responsibility:"" });
  const [actForm, setActForm] = useState({ name:"", description:"", assignee_id:"", co_assignee_ids:[], start_date:"", due_date:"", status:"pendiente", progress:0, dependencies:[] });
  const [milForm, setMilForm] = useState({ name:"", target_date:"" });

  const load = useCallback(async () => {
    const [p, u, a] = await Promise.all([api.get(`/projects/${id}`), api.get("/users"), api.get("/areas")]);
    setProject(p.data); setUsers(u.data); setAreas(a.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!project) return <div className="text-sm text-slate-500">Cargando…</div>;

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const canEdit = user.role === "administrador" || user.role === "coordinador";

  const progress = (project.activities || []).length
    ? Math.round(project.activities.reduce((s, a) => s + (a.progress||0), 0) / project.activities.length)
    : 0;

  const semaforo = () => {
    if (project.status === "finalizado") return { color:"bg-emerald-500", label:"Finalizado" };
    if (!project.end_date) return { color:"bg-slate-300", label:"Sin fecha" };
    const days = Math.round((new Date(project.end_date) - new Date())/(1000*60*60*24));
    if (days < 0) return { color:"bg-crejpp", label:"Retrasado" };
    if (days < 14 && progress < 80) return { color:"bg-amber-500", label:"En riesgo" };
    return { color:"bg-emerald-500", label:"En tiempo" };
  }; 
  const sem = semaforo();

  const addResp = async () => {
    if (!respForm.user_id || !respForm.role_name) { toast.error("Complete los campos"); return; }
    try { await api.post(`/projects/${id}/responsibilities`, respForm); toast.success("Agregado"); setShowResp(false); setRespForm({user_id:"",role_name:"",responsibility:""}); load(); }
    catch (e) { toast.error("Error"); }
  };
  const delResp = async (rid) => { await api.delete(`/projects/${id}/responsibilities/${rid}`); load(); };

  const addActivity = async () => {
    if (!actForm.name) { toast.error("Nombre requerido"); return; }
    try { await api.post(`/projects/${id}/activities`, { ...actForm, assignee_id: actForm.assignee_id||null, start_date: actForm.start_date||null, due_date: actForm.due_date||null }); toast.success("Actividad creada"); setShowAct(false); setActForm({name:"",description:"",assignee_id:"",co_assignee_ids:[],start_date:"",due_date:"",status:"pendiente",progress:0,dependencies:[]}); load(); }
    catch (e) { toast.error("Error"); }
  };
  const updateActivity = async (aid, changes) => {
    const act = project.activities.find(a => a.id === aid);
    try { await api.put(`/projects/${id}/activities/${aid}`, {
      ...act,
      ...changes,
      assignee_id: (changes.assignee_id ?? act.assignee_id) || null,
      start_date: (changes.start_date ?? act.start_date) || null,
      due_date: (changes.due_date ?? act.due_date) || null,
      description: act.description || "",
      co_assignee_ids: act.co_assignee_ids || [],
    }); load(); }
    catch { toast.error("Error"); }
  };
  const delActivity = async (aid) => { if (!window.confirm("¿Eliminar actividad?")) return; await api.delete(`/projects/${id}/activities/${aid}`); load(); };

  const addMilestone = async () => {
    if (!milForm.name || !milForm.target_date) { toast.error("Complete"); return; }
    try { await api.post(`/projects/${id}/milestones`, milForm); toast.success("Hito agregado"); setShowMil(false); setMilForm({name:"",target_date:""}); load(); }
    catch { toast.error("Error"); }
  };
  const delMilestone = async (mid) => { await api.delete(`/projects/${id}/milestones/${mid}`); load(); };

  return (
    <div className="space-y-6">
      <Link to="/proyectos" className="text-sm text-slate-500 hover:text-crejpp inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Volver a proyectos</Link>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${PROJECT_STATUS[project.status].bg} ${PROJECT_STATUS[project.status].text}`}>{PROJECT_STATUS[project.status].label}</span>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500"><span className={`w-2 h-2 rounded-full ${sem.color}`} />{sem.label}</span>
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-2">{project.name}</h1>
            <p className="text-sm text-slate-500 max-w-2xl mt-1">{project.description || "Sin descripción."}</p>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>Inicio: <span className="text-slate-900 font-medium">{formatDate(project.start_date)}</span></div>
            <div>Fin: <span className="text-slate-900 font-medium">{formatDate(project.end_date)}</span></div>
            <div>Líder: <span className="text-slate-900 font-medium">{userMap[project.leader_id] || "—"}</span></div>
            {project.budget && <div>Presupuesto: <span className="text-slate-900 font-medium">USD {project.budget}</span></div>}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">Avance</span><span className="font-semibold">{progress}%</span></div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-crejpp" style={{width:`${progress}%`}} /></div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[
          {k:"actividades", l:"Actividades"},
          {k:"cronograma", l:"Cronograma (Gantt)"},
          {k:"responsabilidades", l:"Responsabilidades"},
          {k:"hitos", l:"Hitos"},
          {k:"cumplimiento", l:"Cumplimiento"},
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab===t.k ? "border-crejpp text-crejpp" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "actividades" && (
        <div className="space-y-3">
          {canEdit && <button onClick={() => setShowAct(true)} data-testid="add-activity-btn" className="inline-flex items-center gap-2 border border-dashed border-slate-300 text-slate-600 hover:border-crejpp hover:text-crejpp text-sm font-medium rounded-md px-4 py-2"><Plus className="w-4 h-4" /> Nueva actividad</button>}
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {(project.activities||[]).map(a => (
              <div key={a.id} className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-slate-900">{a.name}</div>
                  {a.description && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{a.description}</div>}
                  <div className="text-xs text-slate-500 mt-1">Responsable: {userMap[a.assignee_id] || "—"} · {formatDate(a.start_date)} → {formatDate(a.due_date)}</div>
                  {(a.co_assignee_ids || []).length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1 items-center">
                      Corresponsables:
                      {a.co_assignee_ids.map(uid => (
                        <span key={uid} className="bg-red-50 text-crejpp px-1.5 py-0.5 rounded font-medium border border-red-100">{userMap[uid] || uid}</span>
                      ))}
                    </div>
                  )}
                  {a.dependencies?.length > 0 && <div className="text-[11px] text-slate-500 mt-1">Depende de: {a.dependencies.map(d => (project.activities.find(x=>x.id===d)?.name)||d).join(", ")}</div>}
                </div>
                <select disabled={!canEdit} value={a.status} onChange={e => updateActivity(a.id, { status: e.target.value, progress: e.target.value === "cumplido" ? 100 : a.progress })} className="text-xs px-2 py-1 border border-slate-300 rounded-md">
                  {Object.entries(STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <input type="range" min="0" max="100" step="5" disabled={!canEdit} value={a.progress} onChange={e => updateActivity(a.id, { progress: Number(e.target.value) })} className="accent-crejpp w-32" />
                <span className="text-xs font-semibold w-10 text-right">{a.progress}%</span>
                {canEdit && <button onClick={() => delActivity(a.id)} className="text-slate-400 hover:text-crejpp p-1"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {(project.activities||[]).length === 0 && <div className="p-8 text-center text-sm text-slate-400">Aún no hay actividades.</div>}
          </div>
        </div>
      )}

      {tab === "cronograma" && <GanttView project={project} userMap={userMap} />}

      {tab === "responsabilidades" && (
        <div className="space-y-3">
          {canEdit && <button onClick={() => setShowResp(true)} data-testid="add-responsibility-btn" className="inline-flex items-center gap-2 border border-dashed border-slate-300 text-slate-600 hover:border-crejpp hover:text-crejpp text-sm font-medium rounded-md px-4 py-2"><Plus className="w-4 h-4" /> Añadir participante</button>}
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {(project.responsibilities||[]).map(r => (
              <div key={r.id} className="p-4 flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{initials(userMap[r.user_id])}</div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{userMap[r.user_id] || "—"}</div>
                  <div className="text-xs text-slate-500">{r.role_name} · {r.responsibility}</div>
                </div>
                {canEdit && <button onClick={() => delResp(r.id)} className="text-slate-400 hover:text-crejpp p-1"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {(project.responsibilities||[]).length === 0 && <div className="p-8 text-center text-sm text-slate-400">Sin participantes asignados.</div>}
          </div>
        </div>
      )}

      {tab === "hitos" && (
        <div className="space-y-3">
          {canEdit && <button onClick={() => setShowMil(true)} className="inline-flex items-center gap-2 border border-dashed border-slate-300 text-slate-600 hover:border-crejpp hover:text-crejpp text-sm font-medium rounded-md px-4 py-2"><Milestone className="w-4 h-4" /> Nuevo hito</button>}
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {(project.milestones||[]).map(m => (
              <div key={m.id} className="p-4 flex items-center gap-4">
                <Flag className="w-4 h-4 text-crejpp" />
                <div className="flex-1"><div className="font-medium">{m.name}</div><div className="text-xs text-slate-500">{formatDate(m.target_date)}</div></div>
                {canEdit && <button onClick={() => delMilestone(m.id)} className="text-slate-400 hover:text-crejpp p-1"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {(project.milestones||[]).length === 0 && <div className="p-8 text-center text-sm text-slate-400">Sin hitos definidos.</div>}
          </div>
        </div>
      )}

      {tab === "cumplimiento" && <CumplimientoView project={project} userMap={userMap} />}

      {/* Modals */}
      {showResp && (
        <Modal onClose={() => setShowResp(false)} title="Añadir participante">
          <SmallField label="Usuario"><select className="input" data-testid="resp-user" value={respForm.user_id} onChange={e => setRespForm({...respForm, user_id: e.target.value})}><option value="">— Seleccione —</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></SmallField>
          <SmallField label="Rol"><input className="input" data-testid="resp-role" value={respForm.role_name} onChange={e => setRespForm({...respForm, role_name:e.target.value})} placeholder="Ej: Coordinador operativo" /></SmallField>
          <SmallField label="Responsabilidad"><textarea rows={3} className="input" data-testid="resp-text" value={respForm.responsibility} onChange={e => setRespForm({...respForm, responsibility:e.target.value})} /></SmallField>
          <div className="flex justify-end gap-2 mt-4"><button onClick={() => setShowResp(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button><button onClick={addResp} className="px-4 py-2 bg-crejpp text-white rounded-md text-sm font-semibold" data-testid="save-responsibility-btn">Agregar</button></div>
        </Modal>
      )}

      {showAct && (
        <Modal onClose={() => setShowAct(false)} title="Nueva actividad">
          <SmallField label="Nombre"><input className="input" value={actForm.name} onChange={e => setActForm({...actForm, name:e.target.value})} data-testid="activity-name" /></SmallField>
          <SmallField label="Descripción de la actividad">
            <textarea rows={3} className="input" value={actForm.description} onChange={e => setActForm({...actForm, description:e.target.value})} placeholder="Detalle o alcance de la actividad…" data-testid="activity-description" />
          </SmallField>
          <SmallField label="Responsable">
            <select className="input" value={actForm.assignee_id} onChange={e => setActForm({...actForm, assignee_id:e.target.value})} data-testid="activity-assignee">
              <option value="">— Sin asignar —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </SmallField>
          <SmallField label="Corresponsables">
            <div className="flex flex-wrap gap-2 mb-2 min-h-[2rem]">
              {(actForm.co_assignee_ids || []).map(uid => (
                <span key={uid} className="inline-flex items-center gap-1 bg-red-50 text-crejpp text-xs font-medium rounded-full px-2.5 py-1 border border-red-100">
                  {userMap[uid] || uid}
                  <button type="button" onClick={() => setActForm(f => ({...f, co_assignee_ids: f.co_assignee_ids.filter(x => x !== uid)}))} className="hover:text-crejpp-dark" title="Quitar">×</button>
                </span>
              ))}
              {(actForm.co_assignee_ids || []).length === 0 && <span className="text-xs text-slate-400">Ninguno seleccionado</span>}
            </div>
            <select
              className="input"
              value=""
              onChange={(e) => {
                const uid = e.target.value;
                if (!uid) return;
                if (uid === actForm.assignee_id) { toast.error("Ya es responsable principal"); return; }
                setActForm(f => f.co_assignee_ids.includes(uid) ? f : ({...f, co_assignee_ids: [...f.co_assignee_ids, uid]}));
              }}
              data-testid="activity-co-assignee-add"
            >
              <option value="">+ Añadir corresponsable</option>
              {users.filter(u => u.id !== actForm.assignee_id && !(actForm.co_assignee_ids||[]).includes(u.id)).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </SmallField>
          <div className="grid grid-cols-2 gap-2">
            <SmallField label="Inicio"><input type="date" className="input" value={actForm.start_date} onChange={e => setActForm({...actForm, start_date:e.target.value})} /></SmallField>
            <SmallField label="Fin"><input type="date" className="input" value={actForm.due_date} onChange={e => setActForm({...actForm, due_date:e.target.value})} /></SmallField>
          </div>
          <SmallField label="Depende de (opcional)">
            <div className="flex flex-wrap gap-2">
              {(project.activities||[]).map(a => {
                const on = actForm.dependencies.includes(a.id);
                return <button key={a.id} type="button" onClick={() => setActForm(f => ({...f, dependencies: on ? f.dependencies.filter(x=>x!==a.id) : [...f.dependencies, a.id]}))} className={`text-xs px-2 py-1 rounded-full border ${on ? "bg-crejpp text-white border-crejpp" : "border-slate-300 text-slate-700 hover:border-crejpp"}`}>{a.name}</button>;
              })}
            </div>
          </SmallField>
          <div className="flex justify-end gap-2 mt-4"><button onClick={() => setShowAct(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button><button onClick={addActivity} className="px-4 py-2 bg-crejpp text-white rounded-md text-sm font-semibold" data-testid="save-activity-btn">Agregar</button></div>
        </Modal>
      )}

      {showMil && (
        <Modal onClose={() => setShowMil(false)} title="Nuevo hito">
          <SmallField label="Nombre"><input className="input" value={milForm.name} onChange={e => setMilForm({...milForm, name:e.target.value})} /></SmallField>
          <SmallField label="Fecha objetivo"><input type="date" className="input" value={milForm.target_date} onChange={e => setMilForm({...milForm, target_date:e.target.value})} /></SmallField>
          <div className="flex justify-end gap-2 mt-4"><button onClick={() => setShowMil(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button><button onClick={addMilestone} className="px-4 py-2 bg-crejpp text-white rounded-md text-sm font-semibold">Agregar</button></div>
        </Modal>
      )}

      <style>{`.input { width:100%; padding:0.5rem 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; background:white; } .input:focus { outline:none; border-color:#EE1C25; box-shadow:0 0 0 3px rgba(238,28,37,0.12); }`}</style>
    </div>
  );
}

function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white border border-slate-200 rounded-lg w-full max-w-md p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}
function SmallField({ label, children }) { return <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}</span>{children}</label>; }

function GanttView({ project, userMap }) {
  const acts = project.activities || [];
  if (acts.length === 0) return <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-400">Sin actividades para mostrar en el cronograma.</div>;
  const dates = acts.flatMap(a => [a.start_date, a.due_date]).filter(Boolean).map(d => new Date(d).getTime());
  if (dates.length === 0) return <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-400">Agregue fechas a las actividades para ver el cronograma.</div>;
  const min = Math.min(...dates), max = Math.max(...dates);
  const totalDays = Math.max(1, Math.round((max - min) / (1000*60*60*24)) + 1);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 overflow-x-auto">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Cronograma — {totalDays} días</div>
      <div className="min-w-[900px]">
        <div className="flex text-[10px] text-slate-400 mb-2 border-b border-slate-100 pb-2">
          <div className="w-48 shrink-0">Actividad</div>
          <div className="flex-1 relative flex justify-between">
            <span>{formatDate(new Date(min).toISOString())}</span>
            <span>{formatDate(new Date(max).toISOString())}</span>
          </div>
        </div>
        {acts.map((a, idx) => {
          const s = a.start_date ? new Date(a.start_date).getTime() : min;
          const e = a.due_date ? new Date(a.due_date).getTime() : s;
          const left = ((s - min) / (max - min || 1)) * 100;
          const width = Math.max(2, ((e - s) / (max - min || 1)) * 100);
          const meta = STATUS[a.status];
          return (
            <div key={a.id} className="flex items-center gap-2 py-2 border-b border-slate-50">
              <div className="w-48 shrink-0 text-sm truncate">{a.name}</div>
              <div className="flex-1 relative h-6 bg-slate-50 rounded">
                <div className="absolute h-6 rounded flex items-center px-2 text-[10px] text-white font-semibold" style={{ left: `${left}%`, width: `${width}%`, background: meta.color, minWidth: 24 }}>
                  {a.progress}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CumplimientoView({ project, userMap }) {
  const acts = project.activities || [];
  const byUser = {};
  acts.forEach(a => {
    const uid = a.assignee_id || "sin";
    byUser[uid] = byUser[uid] || { total:0, sum:0, cumplido:0 };
    byUser[uid].total += 1;
    byUser[uid].sum += a.progress || 0;
    if (a.status === "cumplido") byUser[uid].cumplido += 1;
  });
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Cumplimiento por responsable</div>
      <div className="space-y-3">
        {Object.entries(byUser).map(([uid, v]) => {
          const avg = v.total ? Math.round(v.sum/v.total) : 0;
          return (
            <div key={uid}>
              <div className="flex justify-between text-sm mb-1">
                <span>{uid === "sin" ? "Sin asignar" : (userMap[uid] || "—")}</span>
                <span className="text-xs text-slate-500">{v.cumplido}/{v.total} cumplidas · avance {avg}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{width:`${avg}%`}} /></div>
            </div>
          );
        })}
        {Object.keys(byUser).length === 0 && <div className="text-sm text-slate-400 text-center py-6">Aún no hay actividades registradas.</div>}
      </div>
    </div>
  );
}
